import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, BellRing } from "lucide-react";
import { getOptIns } from "@/admin/adminApi";
import { LANG_LABELS } from "@/i18n";

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch (e) { return s; }
};

export default function OptIns() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setData(await getOptIns(slug)); } catch (e) { setData({ opt_ins: [], count: 0, total: 0 }); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  const rows = data?.opt_ins || [];

  return (
    <div data-testid="optins-screen" className="space-y-8">
      <div>
        <h2 className="font-display text-3xl font-bold tracking-tight">Opt-ins</h2>
        <p className="text-[#8a8a8a] text-sm mt-1">
          Fans who explicitly opted in to future updates (the closing-block “Keep me in”). These are exactly who your broadcasts can reach.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="rounded-2xl border border-white/10 px-5 py-4 min-w-[140px]">
          <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Active opt-ins</p>
          <p data-testid="optins-active" className="font-display text-3xl font-bold mt-1" style={{ color: "#55E6B3" }}>{data?.count ?? 0}</p>
        </div>
        <div className="rounded-2xl border border-white/10 px-5 py-4 min-w-[140px]">
          <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Total (incl. unsubscribed)</p>
          <p data-testid="optins-total" className="font-display text-3xl font-bold mt-1">{data?.total ?? 0}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#737373]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-white/10 p-10 text-center" data-testid="optins-empty">
          <BellRing className="w-7 h-7 mx-auto mb-3 text-[#555]" />
          <p className="text-[#8a8a8a] text-sm">No opt-ins yet. Fans appear here after tapping “Keep me in” on the release.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-left text-sm" data-testid="optins-table">
            <thead>
              <tr className="border-b border-white/10 font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">
                <th className="px-4 py-3">Fan</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Language</th>
                <th className="px-4 py-3">Opted in</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Platform</th>
                <th className="px-4 py-3">Release</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.fan_id} data-testid={`optin-row-${r.fan_id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-3 text-white">{r.first_name || "—"}</td>
                  <td className="px-4 py-3 text-[#cfcfcf] break-all">{r.email}</td>
                  <td className="px-4 py-3">{LANG_LABELS[r.preferred_language] || (r.preferred_language || "").toUpperCase() || "—"}</td>
                  <td className="px-4 py-3 text-[#a3a3a3] whitespace-nowrap">{fmtDate(r.opted_in_at)}</td>
                  <td className="px-4 py-3 text-[#a3a3a3]">{r.source || "—"}</td>
                  <td className="px-4 py-3 text-[#a3a3a3]">{r.platform || "direct"}</td>
                  <td className="px-4 py-3 text-[#a3a3a3]">{r.release}</td>
                  <td className="px-4 py-3">
                    {r.unsubscribed ? (
                      <span className="font-mono-x text-[10px] tracking-widest uppercase px-2 py-1 rounded" style={{ color: "#FF6B6B", background: "#FF6B6B14" }}>Unsubscribed</span>
                    ) : (
                      <span className="font-mono-x text-[10px] tracking-widest uppercase px-2 py-1 rounded" style={{ color: "#55E6B3", background: "#55E6B314" }}>Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
